import { useDispatch, useSelector } from 'react-redux';
import { rsdFormsActions, useRsdFormsSlice } from './slice';
import { useCallback } from 'react';
import { Moment, MomentType, FormField } from './slice';
import type { RootState } from 'types';

// Custom hooks for RSD forms
export const useRsdForms = () => {
  // Inject the RsdForms slice into the main Redux store
  useRsdFormsSlice();

  const dispatch = useDispatch();
  const {
    moments,
    momentTypes,
    formFields,
    formFieldsCache,
    selectedMomentType,
    loading,
    error,
    allFormConfigs
  } = useSelector(
    (state: RootState) =>
      state.rsdForms || {
        moments: [],
        momentTypes: [],
        formFields: [],
        formFieldsCache: {},
        selectedMomentType: null,
        loading: {
          moments: false,
          momentTypes: false,
          formFields: false,
          allFormConfigs: false
        },
        error: {
          moments: null,
          momentTypes: null,
          formFields: null,
          allFormConfigs: null
        },
        allFormConfigs: null
      }
  );

  const loadMoments = useCallback(() => {
    dispatch(rsdFormsActions.loadMoments());
  }, [dispatch]);

  const loadMomentTypes = useCallback(() => {
    dispatch(rsdFormsActions.loadMomentTypes());
  }, [dispatch]);

  const loadCreateFormData = useCallback(() => {
    dispatch(rsdFormsActions.loadCreateFormData());
  }, [dispatch]);

  const selectMomentType = useCallback(
    (momentTypeId: string) => {
      dispatch(rsdFormsActions.selectMomentType(momentTypeId));
      if (allFormConfigs && allFormConfigs[momentTypeId]) {
        // Extract fields from the cached config
        const formConfig = allFormConfigs[momentTypeId];
        const fields: FormField[] = [];
        formConfig.sections.forEach((section: any) => {
          section.fields.forEach((field: any) => {
            const fieldWithSection: FormField = {
              ...field,
              section: section.heading,
              sectionConfig: {
                columns: section.columns || 1,
                required: (section as any).required,
                subheading: section.subheading,
                toolTipMessage: section.toolTipMessage
              }
            };
            fields.push(fieldWithSection);
          });
        });
        dispatch(rsdFormsActions.formFieldsLoaded({ momentTypeId, fields }));
      }
    },
    [dispatch, allFormConfigs]
  );

  const loadFormFields = useCallback(
    (momentTypeId: string) => {
      // Only load if not already cached
      if (!formFieldsCache[momentTypeId]) {
        dispatch(rsdFormsActions.loadFormFields(momentTypeId));
      }
    },
    [dispatch, formFieldsCache]
  );

  const clearFormFieldsCache = useCallback(() => {
    dispatch(rsdFormsActions.clearFormFieldsCache());
  }, [dispatch]);

  const clearFormFieldsCacheEntry = useCallback(
    (momentTypeId: string) => {
      dispatch(rsdFormsActions.clearFormFieldsCacheEntry(momentTypeId));
    },
    [dispatch]
  );

  const reloadFormFields = useCallback(
    (momentTypeId: string) => {
      // Clear cache for this moment type and reload
      dispatch(rsdFormsActions.clearFormFieldsCacheEntry(momentTypeId));
      dispatch(rsdFormsActions.loadFormFields(momentTypeId));
    },
    [dispatch]
  );

  const createMoment = useCallback(
    (moment: Omit<Moment, 'id' | 'createdAt'>) => {
      console.log(
        '[useRsdForms] Dispatching createMoment action with:',
        moment
      );
      dispatch(rsdFormsActions.createMoment(moment));
    },
    [dispatch]
  );

  const loadAllFormConfigs = useCallback(() => {
    dispatch(rsdFormsActions.loadAllFormConfigs());
  }, [dispatch]);

  return {
    // Data
    moments,
    momentTypes,
    formFields,
    formFieldsCache,
    selectedMomentType,
    allFormConfigs,

    // Loading states
    loadingMoments: loading.moments,
    loadingMomentTypes: loading.momentTypes,
    loadingFormFields: loading.formFields,
    loadingAllFormConfigs: loading.allFormConfigs,

    // Error states
    momentsError: error.moments,
    momentTypesError: error.momentTypes,
    formFieldsError: error.formFields,
    allFormConfigsError: error.allFormConfigs,

    // Actions
    loadMoments,
    loadMomentTypes,
    selectMomentType,
    loadFormFields,
    clearFormFieldsCache,
    clearFormFieldsCacheEntry,
    reloadFormFields,
    createMoment,
    loadAllFormConfigs,

    // Helper for checking if form fields are cached
    isFormFieldsCached: (momentTypeId: string) =>
      !!formFieldsCache[momentTypeId]
  };
};
